import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import './Signup.css';

interface SignupProps {
  onLogin: () => void;
  onBackToLogin?: () => void;
}

const departments = [
  { id: 1, name: 'Civil Engineering', icon: '🏗️' },
  { id: 2, name: 'Electrical Engineering', icon: '⚡' },
  { id: 3, name: 'Mechanical Engineering', icon: '⚙️' },
  { id: 4, name: 'Computer Systems Engineering', icon: '🖥️' },
  { id: 5, name: 'Industrial Engineering', icon: '🏭' },
  { id: 6, name: 'Chemical Engineering', icon: '🧪' },
  { id: 7, name: 'Mechatronics Engineering', icon: '🤖' },
  { id: 8, name: 'Telecommunication Engineering', icon: '📡' },
  { id: 9, name: 'Electronics Engineering', icon: '🔌' },
  { id: 10, name: 'Software Engineering', icon: '💻' },
  { id: 11, name: 'Computer Science', icon: '💾' },
  { id: 12, name: 'Mining Engineering', icon: '⛏️' },
  { id: 13, name: 'Agricultural Engineering', icon: '🌾' },
  { id: 14, name: 'Environmental Engineering', icon: '🌱' },
];

const Signup: React.FC<SignupProps> = ({ onLogin, onBackToLogin }) => {
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    rollNo: '',
    name: '',
    department: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showDepartmentSelector, setShowDepartmentSelector] = useState(false);
  const [selfieData, setSelfieData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState<{ 
    isOpen: boolean; 
    type: 'success' | 'error' | 'info' | 'warning'; 
    title?: string;
    message: string;
    onClose?: () => void;
  }>({ isOpen: false, type: 'info', message: '' });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const showModal = (type: 'success' | 'error' | 'info' | 'warning', message: string, title?: string, onClose?: () => void) => {
    setModalState({ isOpen: true, type, message, title, onClose });
  };

  const validateField = (name: string, value: string) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'rollNo':
        if (value.length < 4) {
          newErrors.rollNo = 'Roll number must be at least 4 characters';
        } else if (!/^[A-Za-z0-9]+$/.test(value)) {
          newErrors.rollNo = 'Roll number can only contain letters and numbers';
        } else {
          delete newErrors.rollNo;
        }
        break;
      
      case 'name':
        if (value.length < 3) {
          newErrors.name = 'Name must be at least 3 characters';
        } else if (!/^[A-Za-z\s]+$/.test(value)) {
          newErrors.name = 'Name can only contain letters';
        } else {
          delete newErrors.name;
        }
        break;
      
      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Please enter a valid email';
        } else {
          delete newErrors.email;
        }
        break;
      
      case 'password':
        if (value.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
        } else {
          delete newErrors.password;
        }
        
        // Show confirm password field as soon as password is entered
        if (value.length > 0) {
          setShowPasswordConfirm(true);
        } else {
          setShowPasswordConfirm(false);
        }
        break;
      
      case 'confirmPassword':
        if (value !== formData.password) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
        break;
    }
    
    setErrors(newErrors);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    validateField(field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.rollNo || !formData.name || !formData.department || !formData.email || !formData.password) {
      alert('Please fill in all fields');
      return;
    }
    
    if (Object.keys(errors).length > 0) {
      alert('Please fix all errors before submitting');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setErrors({ ...errors, confirmPassword: 'Passwords do not match' });
      return;
    }
    
    if (!selfieData || !selfieData.startsWith('data:image')) {
      showModal('error', 'Please upload a profile picture');
      return;
    }
    
    setIsLoading(true);
    try {
      await signup({
        ...formData,
        selfie: selfieData
      });
      // Show success message
      showModal(
        'success', 
        'Your account is pending admin approval. You will be able to login once your account is approved.',
        'Registration Successful!',
        () => {
          if (onBackToLogin) {
            onBackToLogin();
          } else {
            window.location.reload();
          }
        }
      );
    } catch (error: any) {
      setIsLoading(false);
      if (error.message?.includes('already registered') || error.message?.includes('User already registered')) {
        showModal('error', 'This email is already registered. Please login instead.');
        // Navigate to login page
        if (onBackToLogin) {
          onBackToLogin();
        } else {
          window.location.reload();
        }
      } else {
        setErrors({ ...errors, email: error.message || 'Signup failed. Please try again.' });
        showModal('error', error.message || 'Signup failed. Please try again.');
      }
    }
  };


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        showModal('error', 'Please select an image file');
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showModal('error', 'File size too large. Please select an image under 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        setSelfieData(imageData);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <div className="signup-page">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <div className="signup-container">
          <div className="signup-card">
            <div className="signup-header">
              <div className="logo-container">
                <img src="/images/uet-logo.png" alt="UET Logo" className="main-logo" />
              </div>
              <h1>Clean UET</h1>
              <p className="tagline">Join the green revolution</p>
            </div>


            <form onSubmit={handleSubmit} className="signup-form">
              <div className="form-group">
                <label>Roll Number</label>
                <input
                  type="text"
                  placeholder="Enter your roll no."
                  value={formData.rollNo}
                  onChange={(e) => handleInputChange('rollNo', e.target.value)}
                  className={`form-input ${errors.rollNo ? 'error' : ''}`}
                />
                {errors.rollNo && <span className="error-message">{errors.rollNo}</span>}
              </div>

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="Enter your Full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`form-input ${errors.email ? 'error' : ''}`}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Department</label>
                <button
                  type="button"
                  onClick={() => setShowDepartmentSelector(true)}
                  className="department-selector"
                >
                  <span className={formData.department ? 'selected' : 'placeholder'}>
                    {formData.department || 'Select your department'}
                  </span>
                  <span>▼</span>
                </button>
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  placeholder="Min 6 characters"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                />
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>

              {showPasswordConfirm && (
                <div className="form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    placeholder="Re-enter your password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                  />
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              )}

              <div className="form-group">
                <label>Profile Picture</label>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`selfie-capture ${selfieData ? 'captured' : ''}`}
                >
                  {selfieData ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img 
                        src={selfieData} 
                        alt="Profile" 
                        style={{ 
                          width: '80px', 
                          height: '80px', 
                          borderRadius: '50%', 
                          objectFit: 'cover',
                          border: '3px solid #10b981'
                        }} 
                      />
                      <span style={{
                        position: 'absolute',
                        bottom: '-8px',
                        right: '-8px',
                        background: '#10b981',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        border: '2px solid white'
                      }}>✓</span>
                    </div>
                  ) : (
                    <>
                      <span className="capture-icon">📤</span>
                      <span>Upload Photo</span>
                      <span className="capture-hint">Select from gallery</span>
                    </>
                  )}
                </button>
              </div>

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Register & Start Cleaning'}
              </button>
            </form>

            <p className="terms">
              By registering, you agree to follow campus cleaning guidelines
            </p>
            
            <p className="login-link">
              Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); onBackToLogin ? onBackToLogin() : window.location.reload(); }}>Login here</a>
            </p>
            
            <div className="credits">
              <div className="credits-logos">
                <img src="/images/byte-bandit.jpg" alt="ByteBandits" className="credits-logo byte-bandits-logo" />
                <span className="credits-separator">×</span>
                <img src="/images/uet-logo.png" alt="UET" className="credits-logo uet-small-logo" />
              </div>
              <p>Developed by <a href="https://www.instagram.com/byt_ebandits/" target="_blank" rel="noopener noreferrer" className="bytebandits-link"><strong>ByteBandits</strong></a></p>
              <p className="university">UET • University of Engineering and Technology</p>
            </div>
          </div>
        </div>
      </div>

      {/* Department Selector Modal */}
      {showDepartmentSelector && (
        <div className="modal-overlay" onClick={() => setShowDepartmentSelector(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Your Department</h3>
              <p>Choose from available departments</p>
            </div>
            
            <div className="departments-grid">
              {departments.map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => {
                    setFormData({...formData, department: dept.name});
                    setShowDepartmentSelector(false);
                  }}
                  className={`department-option ${formData.department === dept.name ? 'selected' : ''}`}
                >
                  <span className="dept-icon">{dept.icon}</span>
                  <span className="dept-name">{dept.name}</span>
                  {formData.department === dept.name && <span className="checkmark">✓</span>}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowDepartmentSelector(false)}
              className="modal-close"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      <Modal
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          if (modalState.onClose) {
            modalState.onClose();
          }
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
    </>
  );
};

export default Signup;